// types/navigation.ts
export interface NavigationItem {
  label: string;
  icon?: string;
  href?: string;
  children?: NavigationItem[];
}

export interface NavigationConfig {
  logo: {
    letter: string;
    text: string;
    bgColor: string;
    textColor: string;
  };
  searchPlaceholder: string;
  searchButtonColor: string;
  primaryNavItems: NavigationItem[];
  userInfo: {
    name: string;
    subtitle: string;
    avatarBg: string;
    avatarTextColor: string;
  };
  userMenuItems: NavigationItem[];
  rightButtons?: {
    favorites?: boolean;
    cart?: boolean;
    notifications?: boolean;
    /** Buyer/seller DM entry in header (badge + unread list when wired from the app). */
    messages?: boolean;
  };
  badges?: {
    cart?: number;
    notifications?: number;
  };
}

// config/navigationConfig.ts
export const navigationConfigs: Record<string, NavigationConfig> = {
  admin: {
    logo: {
      letter: "BI",
      text: "BizcoverIndia.com",
      bgColor: "bg-destructive",
      textColor: "text-destructive-foreground",
    },
    searchPlaceholder: "Search users, products, orders...",
    searchButtonColor: "bg-destructive hover:bg-destructive/90",
    primaryNavItems: [],
    userInfo: {
      name: "Admin User",
      subtitle: "System Administrator",
      avatarBg: "bg-destructive",
      avatarTextColor: "text-destructive-foreground",
    },
    userMenuItems: [
      { label: "Profile", href: "/admin/profile_settings", icon: "User" },
      { label: "Logout", href: "/logout" },
    ],
    rightButtons: { notifications: true },
    badges: { notifications: 12 },
  },

  buyer: {
    logo: {
      letter: "BI",
      text: "BizcoverIndia.com",
      bgColor: "bg-primary",
      textColor: "text-primary-foreground",
    },
    searchPlaceholder: "Search for products, suppliers...",
    searchButtonColor: "bg-primary hover:bg-primary-dark",
    primaryNavItems: [
      {
        label: "Categories",
        icon: "Package",
        href: "/categories",
      },
      {
        label: "All products",
        icon: "Package",
        href: "/search",
      },
      {
        label: "Sellers",
        icon: "Users",
        href: "/sellers",
      },
    ],
    userInfo: {
      name: "John Buyer",
      subtitle: "Premium Member",
      avatarBg: "bg-primary",
      avatarTextColor: "text-primary-foreground",
    },
    userMenuItems: [
      { label: "My Dashboard", href: "/buyer/dashboard" },
      { label: "Profile", href: "/buyer/profile_settings", icon: "User" },
      { label: "Messages", href: "/chat", icon: "Mail" },
      { label: "Logout", href: "/logout" },
    ],
    rightButtons: {
      // cart: true,
      notifications: true,
      messages: true,
    },
    badges: { cart: 3, notifications: 2 },
  },

  seller: {
    logo: {
      letter: "BI",
      text: "BizcoverIndia.com",
      bgColor: "bg-success",
      textColor: "text-success-foreground",
    },
    searchPlaceholder: "Search products or RFQs...",
    searchButtonColor: "bg-success hover:bg-success/90",
    primaryNavItems: [
      {
        label: "Categories",
        icon: "Package",
        href: "/categories",
      },
      {
        label: "All products",
        icon: "Package",
        href: "/search",
      },
      {
        label: "Sellers",
        icon: "Users",
        href: "/sellers",
      },
      {
        label: "Pricing",
        icon: "CreditCard",
        href: "/seller/membership_plans",
      },
    ],
    userInfo: {
      name: "Jane Seller",
      subtitle: "Verified Supplier",
      avatarBg: "bg-success",
      avatarTextColor: "text-success-foreground",
    },
    userMenuItems: [
      { label: "Storefront", href: "/seller/store_front", icon: "Store" },
      { label: "Messages", href: "/chat", icon: "Mail" },
      { label: "Logout", href: "/logout" },
    ],
    rightButtons: { notifications: true, messages: true },
    badges: { notifications: 5 },
  },

  // Both role uses seller config (no Categories, All products, Sellers, Pricing buttons)
  both: {
    logo: {
      letter: "BI",
      text: "BizcoverIndia.com",
      bgColor: "bg-success",
      textColor: "text-success-foreground",
    },
    searchPlaceholder: "Search products or RFQs...",
    searchButtonColor: "bg-success hover:bg-success/90",
    primaryNavItems: [],
    userInfo: {
      name: "Jane Seller",
      subtitle: "Verified Supplier",
      avatarBg: "bg-success",
      avatarTextColor: "text-success-foreground",
    },
    userMenuItems: [
      { label: "Storefront", href: "/seller/store_front", icon: "Store" },
      { label: "Messages", href: "/chat", icon: "Mail" },
      { label: "Logout", href: "/logout" },
    ],
    rightButtons: { notifications: true, messages: true },
    badges: { notifications: 5 },
  },
};

/**
 * Seller profile (user) menu items driven by same logic as sidebar (3-step onboarding).
 * - Incomplete profile: Business Profile + Logout only.
 * - Step 1 done, Step 2 not done: Business Profile, Membership Plans, Logout (no Storefront).
 * - Step 1+2 done: add Storefront.
 * - Approved: full menu (Business Profile, Membership Plans, Messages, Storefront, Logout).
 */
export function getSellerUserMenuItems(
  sellerApproved: boolean,
  profileCompleteWithoutSocial: boolean,
  hasMembershipPlanSelected = false
): NavigationItem[] {
  if (!profileCompleteWithoutSocial) {
    return [
      { label: "Business Profile", href: "/seller/profile_settings", icon: "User" },
      { label: "Logout", href: "/logout" },
    ];
  }
  if (!sellerApproved) {
    const items: NavigationItem[] = [
      { label: "Business Profile", href: "/seller/profile_settings", icon: "User" },
      { label: "Membership Plans", href: "/seller/membership_plans", icon: "CreditCard" },
      { label: "Logout", href: "/logout" },
    ];
    if (hasMembershipPlanSelected) {
      items.splice(2, 0, { label: "Storefront", href: "/seller/store_front", icon: "Store" });
    }
    return items;
  }
  return [
    { label: "Business Profile", href: "/seller/profile_settings", icon: "User" },
    { label: "Membership Plans", href: "/seller/membership_plans", icon: "CreditCard" },
    { label: "Messages", href: "/chat", icon: "Mail" },
    { label: "Storefront", href: "/seller/store_front", icon: "Store" },
    { label: "Logout", href: "/logout" },
  ];
}
